/**
 * Часть плана для позиции чека (§2.2, §10.1).
 *
 * Чек из магазина почти всегда смешанный: хлеб, молоко и мясо в одном пакете
 * со сладостями, газировкой и чипсами. Отнести весь чек к одной части плана
 * значит либо записать конфеты в необходимое, либо продукты — в необязательное.
 * И то и другое ломает главный вопрос продукта: без чего нельзя и от чего
 * можно отказаться.
 *
 * Разбор словарный, а не модельный. Так решено по трём причинам: он мгновенный
 * и бесплатный (каскад Л1 — классификация локально), он одинаков между
 * запусками, и его видно целиком — список слов можно прочитать и поправить, в
 * отличие от ответа модели. Точности словаря хватает на типовую корзину, а
 * промахи человек исправляет тапом, потому что позиции показываются до записи.
 *
 * Умолчание — «повседневные нужды». В продуктовой корзине необходимого больше, и ошибка
 * в эту сторону безопаснее: записать нужное в необязательное значит предложить
 * человеку отказаться от еды.
 */
import type { PlanPart } from './types.js';

/**
 * Слова, которые выглядят как признак, но им не являются.
 *
 * Проверяются раньше всех: «свинина» начинается на «вин», «печень» — на «печ»,
 * «виноград» — на «вино». Без этого списка мясо уехало бы в необязательное.
 */
const EXCEPT = [
  'виноград', 'свинин', 'печень', 'винегрет', 'печенк',
  // Найдено на настоящих чеках: «Леп.ТОРТИЛ.пшен.» уходила в необязательное,
  // потому что «ТОРТИЛ» начинается на «торт», а детское пюре «ябл-бан-печ» —
  // потому что «печ» читается как «печенье».
  'тортил', 'пюре', 'агуша', 'фрутоняня', 'детск',
];

/**
 * Необязательное. Список проверяется раньше необходимого: в составном названии
 * «шоколад молочный» слово «молоч» нашлось бы и там, а решает здесь сладость.
 */
const WANT = [
  // сладкое
  'шоколад', 'конфет', 'печен', 'печ', 'вафл', 'зефир', 'мармелад', 'пастил', 'халв',
  'торт', 'пирожн', 'десерт', 'морожен', 'сгущен', 'батончик', 'сникерс', 'твикс',
  'киндер', 'орео', 'пряник', 'круассан', 'донат', 'маффин', 'кекс', 'сырок',
  'глазирован', 'джем', 'варень', 'нутелл', 'ирис', 'леденц', 'жвач', 'жеват',
  'ватрушк', 'мюсл', 'шок', 'булочк', 'слойк', 'рулет',
  // снеки
  'чипс', 'сухар', 'гренк', 'снек', 'попкорн', 'начос', 'крендел', 'кренд',
  'соломк', 'палочки кукуруз', 'фисташк', 'арахис солен',
  // напитки не первой необходимости
  'газиров', 'газ', 'кола', 'пепси', 'фанта', 'спрайт', 'лимонад', 'энергет', 'редбул',
  'тоник', 'квас', 'морс',
  // алкоголь и табак
  'пив', 'вино', 'винн', 'водк', 'коньяк', 'виск', 'ликер', 'шампан', 'сидр',
  'алкогол', 'сигарет', 'табак', 'вейп', 'кальян',
  // прочее необязательное
  'декоратив', 'сувенир', 'игрушк', 'открытк', 'цветы', 'букет',
];

/**
 * Необходимое.
 *
 * На часть плана этот список не влияет — незнакомое и так считается
 * необходимым. Он отвечает на другой вопрос: узнал ли разбор товар вообще.
 * Неузнанное помечается на экране, и человек проверяет пять строк вместо
 * двадцати двух.
 */
const NEED = [
  // базовая еда
  'хлеб', 'батон', 'молок', 'кефир', 'ряженк', 'йогурт', 'творог', 'творож',
  'сметан', 'сыр', 'масл', 'яйц', 'мяс', 'куриц', 'филе', 'фарш', 'говядин',
  'свинин', 'индейк', 'рыб', 'сельдь', 'ветчин', 'колбас', 'сосиск', 'краб',
  'круп', 'греч', 'рис', 'макарон', 'вермишел', 'мук', 'сахар', 'сол',
  'картоф', 'морков', 'лук', 'капуст', 'свекл', 'помидор', 'огурц', 'перец',
  'яблок', 'банан', 'апельсин', 'мандарин', 'груш', 'лимон', 'нектарин', 'персик',
  'овощ', 'фрукт', 'зелен', 'укроп', 'петрушк', 'чеснок',
  'вод', 'чай', 'кофе', 'какао',
  // дом и дети
  'подгузник', 'пеленк', 'салфетк', 'туалетн', 'бумаг', 'порошок', 'моющ',
  'мыл', 'шампун', 'зубн', 'щетк', 'губк', 'пакет', 'корм', 'наполнител',
  'лекарств', 'бинт', 'пластыр',
];

/** Название в сравнимый вид: без регистра, ё и знаков. */
const normalize = (name: string): string =>
  name.toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Совпадение по началу слова, а не по подстроке.
 *
 * Подстрока находила «вин» внутри «свинины» и «печ» внутри «печени», то есть
 * относила мясо к необязательному. Признак из нескольких слов сравнивается
 * целиком: «палочки кукурузные» — снек, «палочки крабовые» — еда.
 */
function hits(words: string[], full: string, markers: string[]): boolean {
  for (const m of markers) {
    if (m.includes(' ')) { if (full.includes(m)) return true; continue; }
    for (const w of words) if (w.startsWith(m)) return true;
  }
  return false;
}

export interface ItemGuess {
  part: PlanPart;
  /**
   * Узнал ли словарь товар. Ложь означает «отнесено к необходимому по
   * умолчанию» — это стоит показать, а не выдавать за решение.
   */
  known: boolean;
}

export function classifyItem(name: string): ItemGuess {
  const n = normalize(name);
  if (n === '') return { part: 'need', known: false };
  const words = n.split(' ');
  if (hits(words, n, EXCEPT)) return { part: 'need', known: true };
  if (hits(words, n, WANT)) return { part: 'want', known: true };
  if (hits(words, n, NEED)) return { part: 'need', known: true };
  return { part: 'need', known: false };
}

export const classifyItemPart = (name: string): PlanPart => classifyItem(name).part;

export interface BasketSplit {
  need: number;
  want: number;
}

/**
 * Суммы по частям плана.
 *
 * Складывает копейки целыми числами: сумма частей обязана совпасть с итогом
 * чека до копейки, иначе разбиение исказит и план, и аналитику.
 */
export function splitBasket(
  items: readonly { sum: number; part: PlanPart }[],
): BasketSplit {
  let need = 0;
  let want = 0;
  for (const it of items) {
    if (it.part === 'want') want += it.sum;
    else need += it.sum;
  }
  return { need, want };
}
